import { HttpClient, HttpEvent, HttpEventType, HttpHeaders } from "@angular/common/http";
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { from, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from "rxjs/operators";
import { environment } from '../../../environments/environment';
import { isNotNullOrUndefined } from '../../helper/helper';
import { UploadFileArgs } from '../entities/file.entity';
import { VersionEntity } from '../entities/version.entity';
import { DaveMutation, Query } from '../graphql-types';
import { retryWithBackoff } from '../helper/http-service.helper';
import { State } from '../State';
import { getToken } from '../State/selectors/base.selectors';
import { ErrorPopupService } from '../../error-popup/error-popup.service';
import { PublicVersionEntity } from '../entities/public-version.entity';

/**
 * Dieser Service benötigt Konfigurationen, daher sollte eine Factory genutzt werden
 * der provider könnte zum Beispiel so außsehen:
 * {  provide: HttpService,
 *    useFactory: (httpClient: HttpClient) =>
 *      httpServiceFactory(httpClient, HTTP_SERVICE_CONFIG),
 *    deps: [HttpClient]
 * },
 */

export function DownloadVersions(versions: VersionEntity[], token?: string) {
    let timer = 0;
    for (const v of versions) {
        setTimeout(() => {
            const link = document.createElement('a');
            link.download = '';
            link.href = HttpService.getUrl(v.GetDownloadLink(token || localStorage.getItem('token')), 'file');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }, timer);
        timer += 2500;
    }
}

export function DownloadZip(fileIds: number[], folderIds: number[]) {
    const url = new URL(HttpService.getUrl(`/files/download/zip`, 'file'));
    url.searchParams.set('token', localStorage.getItem('token'));
    fileIds?.length && url.searchParams.set('file_ids', fileIds.join(','));
    folderIds?.length && url.searchParams.set('folder_ids', folderIds.join(','));
    const link = document.createElement('a');
    link.download = '';
    link.href = url.toString();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}
type apiPoints = 'file' | 'gateway' | 'sfdt'
@Injectable({
    providedIn: 'root',
})
export class HttpService {
    constructor(private http: HttpClient, private store$: Store<State>, private errorPopup: ErrorPopupService) {}

    public static getUrl(url: string, service: apiPoints = 'gateway', id: number = -1) {
        let _service: string = service;
        if (url && url.charAt(0) !== '/') {
            url = '/' + url;
        }
        if (_service && _service.charAt(0) !== '/') {
            _service = '/' + _service;
        }

        url = environment.gatewayApiUrl + _service + url;

        //fix for local gateway
        // url = url.replace('/gateway', '');

        if (id > -1) {
            if (url.indexOf('?') === -1) {
                url += '?Id=' + id;
            } else {
                url += '&Id=' + id;
            }
        }
        return url;
    }

    public static extractData(body: any) {
        if (body && body.data) {
            return body.data as Query & DaveMutation;
        }
        return null;
    }

    public GetUrl(url: string, service?: apiPoints, id: number = -1) {
        return HttpService.getUrl(url, service, id);
    }

    public graphQlWithProgress(query: { query: string; variables?: object }, options?: { token?: string, retry?: boolean}, service: apiPoints = 'gateway', customUrl?: string): Observable<HttpEvent<Object>> {
        return this._graphQl(query, options, service, customUrl, true) as Observable<HttpEvent<Object>>;
    }
    public graphQl(query: { query: string; variables?: object }, options?: { token?: string, retry?: boolean, useCompression?: boolean}, service: apiPoints = 'gateway', customUrl?: string) {
        return this._graphQl(query, options, service, customUrl).pipe(
            map(body => HttpService.extractData(body)),
        );
    }
    private _graphQl(
        query: { query: string; variables?: object },
        options?: { token?: string, retry?: boolean, useCompression?: boolean },
        service: apiPoints = 'gateway',
        customUrl?: string,
        withProgress = false
    ) {
        let headers = new HttpHeaders();
        const token = options?.token || localStorage.getItem('token');

        if (token) {
            headers = headers.set('Authorization', 'Bearer ' + token);
        }

        const jsonBody = JSON.stringify(query);
        const url = customUrl || this.GetUrl('query', service);

        const useCompression = !!options?.useCompression;

        let req$: Observable<any>;

        if (useCompression && typeof window !== 'undefined' && 'CompressionStream' in window) {
            // Asynchrone Kompression über CompressionStream -> Blob -> dann HttpClient
            const compressedBlob$ = from(this.compressJsonWithCompressionStream(jsonBody));

            req$ = compressedBlob$.pipe(
                map((compressedBlob) => {
                    headers = headers
                        .set('Content-Encoding', 'gzip')
                        .set('Content-Type', 'application/json');
                    return compressedBlob as Blob;
                }),
                switchMap((bodyToSend: Blob) =>
                    withProgress
                        ? this.graphQlPostWithProgressEvents(url, bodyToSend, headers, true)
                        : this.graphQlPost(url, bodyToSend, headers, true),
                ),
            );
        } else {
            // Fallback: keine Kompression oder CompressionStream nicht verfügbar
            let bodyToSend: string | Blob = jsonBody;

            headers = headers.set('Content-Type', 'application/json');

            req$ = withProgress
                ? this.graphQlPostWithProgressEvents(url, bodyToSend, headers, false)
                : this.graphQlPost(url, bodyToSend, headers, false);
        }

        if (options?.retry) {
            return req$.pipe(
                retryWithBackoff(),
                catchError((err, caught) => {
                    console.error({ err, bodyToSend: jsonBody });
                    this.errorPopup.OpenErrorPopup();
                    return of(null);
                }),
            );
        } else {
            return req$.pipe(
                catchError((err, caught) => {
                    console.error({ err, bodyToSend: jsonBody });
                    return of(null);
                }),
            );
        }
    }

    private graphQlPost(url: string, bodyData: string | Blob, headers: HttpHeaders, useCompression: boolean = false): Observable<any> {
        if (useCompression) {
            return this.http.post(url, bodyData, {
                headers,
                responseType: 'arraybuffer',
                observe: 'response',
            }).pipe(
                map((response) => {
                    if (!response.body) {
                        console.log('No response body');
                        return null;
                    }
                        const text = new TextDecoder().decode(response.body);
                        return JSON.parse(text);
                })
            );
        }

        // Kein gzip
        return this.http.post(url, bodyData, { headers });
    }

    private graphQlPostWithProgressEvents(url: string, bodyData: string | Blob, headers: HttpHeaders, useCompression: boolean = false): Observable<HttpEvent<any>> {
        if (useCompression) {
            return this.http.post(url, bodyData, {
                headers,
                reportProgress: true,
                observe: 'events',
                responseType: 'arraybuffer',
            }).pipe(
                map((event: HttpEvent<ArrayBuffer>) => {
                    if (event.type === HttpEventType.Response && event.body) {
                        const text = new TextDecoder().decode(event.body);
                        return {
                            ...event,
                            body: JSON.parse(text),
                        } as HttpEvent<any>;
                    }
                    return event as HttpEvent<any>;
                })
            );
        }

        // ohne Kompression
        return this.http.post(url, bodyData, {
            headers,
            reportProgress: true,
            observe: 'events',
        });
    }

    private async compressJsonWithCompressionStream(jsonBody: string): Promise<Blob> {
        const encoder = new TextEncoder();
        const uint8 = encoder.encode(jsonBody);
        const inputBlob = new Blob([uint8], { type: 'application/json' });

        // Stream aus dem Blob erzeugen
        const inputStream = inputBlob.stream();
        // @ts-ignore: CompressionStream ist u.U. noch nicht im TS-Typ enthalten
        const compressedStream = inputStream.pipeThrough(new CompressionStream('gzip'));

        // Den komprimierten Stream wieder in einen Blob materialisieren
        const compressedBlob = await new Response(compressedStream).blob();
        return compressedBlob;
    }

    public download(v: VersionEntity | PublicVersionEntity, options?: { token?: string }, service: apiPoints = 'file'): Observable<Blob> {
        let headers = new HttpHeaders();
        if (options && options.token) {
            headers = headers.set('Authorization', 'Bearer ' + options.token);
        } else {
            headers = headers.set('Authorization', 'Bearer ' + localStorage.getItem('token'));
        }
        return this.http.get(this.GetUrl((v instanceof PublicVersionEntity) ? v.DownloadPath : v.GetDownloadLink(options?.token || localStorage.getItem('token')), service), {
            headers,
            responseType: 'blob',
        });
    }

    /**
     * Lädt eine Bilddatei hoch.
     *
     * @throws Gibt die Fehler des HttpClients ungefiltert durch.
     */
    public UploadFile(payload: UploadFileArgs) {
        const uploadPath = 'files';
        const formData = new FormData();

        formData.append('files', payload.file, payload.name);
        payload.documentId && formData.append('documentId', payload.documentId + '');
        payload.description && formData.append('description', payload.description);
        payload.name && formData.append('name', payload.name);
        payload.customerIds && formData.append('customerIds', JSON.stringify(payload.customerIds));
        payload.commissionIds && formData.append('commissionIds', JSON.stringify(payload.commissionIds));
        payload.transmissionIds && formData.append('transmissionIds', JSON.stringify(payload.transmissionIds));
        payload.tagIds && formData.append('tagIds', JSON.stringify(payload.tagIds));
        payload.hidden && formData.append('hidden', JSON.stringify(payload.hidden));
        payload.eventIds && formData.append('eventIds', JSON.stringify(payload.eventIds));

        return this.store$.select(getToken).pipe(
            filter(isNotNullOrUndefined),
            take(1),
            switchMap((token) =>
                this.http
                    .post(this.GetUrl(uploadPath, 'gateway'), formData, {
                        headers: {
                            Accept: 'application/json',
                            Authorization: `Bearer ${token}`,
                        },
                    })
                    .pipe(map((res) => res as { data?: any })),
            ),
        );
    }
}
